import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

const LINKS = [
  { href: "#lineup", label: "Lineup" },
  { href: "#passes", label: "Tickets" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      data-testid="site-header"
      initial={{ y: -40, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.9, ease: [0.77, 0, 0.175, 1] }}
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-[rgba(12,10,9,0.72)] backdrop-blur-xl border-b border-white/5"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 lg:h-20 flex items-center justify-between">
        <a
          href="#top"
          data-testid="nav-logo"
          className="font-display text-lg lg:text-xl font-black tracking-tighter text-[#FFF7ED] flex items-center gap-2"
        >
          <span className="inline-block h-2 w-2 rounded-full bg-[#EA580C] shadow-[0_0_12px_#EA580C]" />
          SUNBURN
          <span className="font-body text-[10px] tracking-[0.3em] text-[#F59E0B]/70 ml-1">
            / BLR 22
          </span>
        </a>

        <nav className="hidden lg:flex items-center gap-10">
          {LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-${l.label.toLowerCase()}-link`}
              className="nav-link font-body text-[13px] uppercase tracking-[0.2em] text-[#FFF7ED]/80 hover:text-[#FFF7ED]"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="hidden lg:block">
          <a
            href="#passes"
            data-testid="nav-tickets-cta"
            className="inline-flex items-center gap-2 bg-[#EA580C] text-white px-5 py-2.5 text-[12px] font-bold uppercase tracking-[0.22em] hover:bg-[#F59E0B] hover:text-[#0C0A09] transition-colors duration-300"
          >
            Get Tickets
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
              <path
                d="M5 12h14M13 5l7 7-7 7"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="square"
              />
            </svg>
          </a>
        </div>

        {/* Mobile toggle */}
        <button
          data-testid="mobile-menu-toggle"
          aria-label="Menu"
          onClick={() => setOpen((s) => !s)}
          className="lg:hidden w-10 h-10 grid place-items-center border border-white/15 text-[#FFF7ED]"
        >
          <span className="sr-only">Toggle menu</span>
          <div className="flex flex-col gap-1.5">
            <span
              className={`block h-[1.5px] w-5 bg-[#FFF7ED] transition-transform ${
                open ? "translate-y-[3px] rotate-45" : ""
              }`}
            />
            <span
              className={`block h-[1.5px] w-5 bg-[#FFF7ED] transition-transform ${
                open ? "-translate-y-[3px] -rotate-45" : ""
              }`}
            />
          </div>
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="lg:hidden overflow-hidden bg-[rgba(12,10,9,0.95)] backdrop-blur-xl border-t border-white/5"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {LINKS.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={() => setOpen(false)}
                  data-testid={`mobile-nav-${l.label.toLowerCase()}`}
                  className="font-display text-xl font-bold text-[#FFF7ED]"
                >
                  {l.label}
                </a>
              ))}
              <a
                href="#passes"
                onClick={() => setOpen(false)}
                data-testid="mobile-nav-tickets-cta"
                className="mt-2 inline-flex items-center justify-center bg-[#EA580C] text-white py-3 font-bold uppercase tracking-[0.22em] text-xs"
              >
                Get Tickets
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
